import { format } from 'date-fns';
import { parseTripDate } from './tripUtils';

/**
 * Label for trip picker / assigned trip: "Title · Mon, Oct 3 – Fri, Oct 7, 2026"
 */
export function formatTripOptionLabel(trip) {
  if (!trip) return '';
  const title = String(trip.title || '').trim() || `Trip #${trip.id}`;
  const range = formatTripDateRangeLabel(trip.start_date, trip.end_date);
  return range ? `${title} · ${range}` : title;
}

/**
 * Destination for Email Imports: "Cruise World → Trip itinerary"
 * or "Cruise World → Personal itinerary (Nabeel Kamboh)"
 */
export function formatImportDestinationLabel(item, t) {
  if (!item?.trip_title && !item?.trip_id) return '';
  const tripName = String(item.trip_title || `Trip #${item.trip_id}`).trim();
  const range = formatTripDateRangeLabel(item.trip_start_date, item.trip_end_date);
  const tripPart = range ? `${tripName} · ${range}` : tripName;
  if (!item.placement) return tripPart;
  const placementLabel = item.placement === 'personal'
    ? t('emailForward.placementPersonal')
    : t('emailForward.placementShared');
  const owner = item.placement === 'personal' && item.placement_owner_name
    ? ` (${item.placement_owner_name})`
    : '';
  return `${tripPart} → ${placementLabel}${owner}`;
}

export function formatTripDateRangeLabel(startDate, endDate) {
  const start = parseTripDate(startDate);
  const end = parseTripDate(endDate);
  if (!start && !end) return '';
  if (start && !end) return format(start, 'MMM d, yyyy');
  if (!start && end) return format(end, 'MMM d, yyyy');
  if (start.getFullYear() === end.getFullYear()) {
    return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
  }
  return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
}

function parseParsedJson(parsedJson) {
  if (!parsedJson) return null;
  if (typeof parsedJson === 'object') return parsedJson;
  try {
    return JSON.parse(parsedJson);
  } catch (_) {
    return null;
  }
}

export function getImportReservations(item) {
  const parsed = parseParsedJson(item?.parsed_json);
  const reservations = parsed?.reservations;
  return Array.isArray(reservations) ? reservations : [];
}

function shortDateTime(value) {
  if (!value) return '';
  const str = String(value).trim();
  const dateKey = str.includes('T') ? str.split('T')[0] : str.split(/\s+/)[0];
  const date = parseTripDate(dateKey) || new Date(str);
  if (!date || Number.isNaN(date.getTime())) return '';
  const hasTime = /\d{1,2}:\d{2}/.test(str);
  if (!hasTime) return format(date, 'MMM d, yyyy');
  // Prefer local wall-clock from parsed YYYY-MM-DD + time when present
  const timeMatch = str.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (timeMatch && parseTripDate(dateKey)) {
    const withTime = parseTripDate(dateKey);
    withTime.setHours(Number(timeMatch[1]), Number(timeMatch[2]), Number(timeMatch[3] || 0));
    return format(withTime, 'MMM d, yyyy h:mm a');
  }
  return format(date, 'MMM d, yyyy h:mm a');
}

function flightRoute(reservation) {
  const data = reservation?.transportationData || {};
  const from = data.departureAirport || data.origin || '';
  const to = data.arrivalAirport || data.destination || '';
  if (from && to) return `${from} → ${to}`;
  if (reservation?.location) return String(reservation.location);
  return '';
}

function reservationKind(reservation) {
  const raw = String(
    reservation?.type
      || reservation?.category
      || reservation?.transportationType
      || ''
  ).toLowerCase();
  if (raw.includes('flight')) return 'flight';
  if (raw.includes('hotel') || raw.includes('lodging')) return 'hotel';
  if (raw.includes('car')) return 'car';
  if (raw.includes('restaurant')) return 'restaurant';
  if (raw.includes('event')) return 'event';
  return raw || 'booking';
}

/**
 * One human-readable line per reservation for Email Imports review UI.
 */
export function summarizeReservation(reservation) {
  if (!reservation) return '';
  const kind = reservationKind(reservation);
  const conf = reservation.confirmationNumber
    ? `Conf ${reservation.confirmationNumber}`
    : '';
  const provider = reservation.provider || reservation.transportationData?.airline
    || reservation.transportationData?.company || '';
  const name = String(reservation.name || '').trim();
  const start = shortDateTime(reservation.startDatetime || reservation.start_datetime);
  const end = shortDateTime(reservation.endDatetime || reservation.end_datetime);
  const datePart = start && end && start !== end ? `${start} – ${end}` : (start || end);

  const travelers = (reservation.travelers || [])
    .map((person) => person?.name || person?.email)
    .filter(Boolean)
    .slice(0, 3);
  const travelerPart = travelers.length ? travelers.join(', ') : '';

  let core = '';
  if (kind === 'flight') {
    const flightNo = reservation.transportationData?.flightNumber
      || reservation.transportationData?.flight_number
      || '';
    const route = flightRoute(reservation);
    const airline = provider || name;
    core = [airline, flightNo, route].filter(Boolean).join(' · ');
    if (!core) core = name || 'Flight';
  } else if (kind === 'hotel') {
    core = name || provider || reservation.location || 'Hotel';
  } else if (kind === 'car') {
    core = [provider || name || 'Car rental', reservation.transportationData?.pickupLocation]
      .filter(Boolean)
      .join(' · ');
  } else {
    core = name || provider || reservation.location || kind;
  }

  return [core, datePart, conf, travelerPart].filter(Boolean).join(' · ');
}

export function summarizeImportReservations(item, { limit = 6 } = {}) {
  const reservations = getImportReservations(item);
  if (!reservations.length) return [];
  return reservations
    .map(summarizeReservation)
    .filter(Boolean)
    .slice(0, limit);
}
